// src/pages/documents/EntityRowSelector.tsx

import React from 'react';
import { Input, Select, Space, Typography } from 'antd';
import { MappableEntity } from '../../types';
import { BENEFICIARY_LEVELS } from '../participant/beneficiaryLevels';

const { Text } = Typography;
const { Option } = Select;

/**
 * Picks which row a mapping means, for entities an estate holds several of.
 *
 * An estate has many assets, many participants, many liabilities, so naming the
 * entity and the field does not say what fills the box: "asset market value" is
 * not an answer while there are four assets. This control supplies the missing
 * half of the answer.
 *
 * Rows are picked by position or by a matching value, never by id: a document is
 * filled against whichever estate is chosen at download time, so an id would
 * only ever match one estate. The two forms the backend understands:
 *
 *   "[1]"                                zero-based position
 *   "[relationshipToDecedent=Spouse]"    first row whose field matches
 *
 * An empty value means the first row, which is how mappings behaved before a
 * row could be chosen.
 */

export interface EntityRowSelectorProps {
  entity: MappableEntity;
  entityKey?: string;
  value: string;
  onChange: (selector: string) => void;
  /**
   * Changes when the control is pointed at a different mapping, so the choice
   * between position and matching is re-read from that mapping's selector.
   * Without it, editing one field and then another would carry the first
   * field's mode across.
   */
  resetKey?: string | number;
  /** Hides the explanatory line where space is tight, as in a table row. */
  compact?: boolean;
}

export const parseMatch = (selector: string): { field: string; value: string } => {
  const clean = (selector ?? '').trim().replace(/^\[/, '').replace(/\]$/, '');
  const equals = clean.indexOf('=');
  if (equals <= 0) return { field: '', value: '' };

  return {
    field: clean.slice(0, equals).trim(),
    value: clean.slice(equals + 1).trim(),
  };
};

const PARTICIPANT_POSITION_LABELS: Record<number, string> = {
  0: 'Primary Beneficiary (1st)',
  1: 'Secondary Beneficiary (2nd)',
  2: 'Tertiary Beneficiary (3rd)',
  3: 'Quaternary Beneficiary (4th)',
};

/**
 * Formats a selector into a human-readable label for previews and summaries.
 */
export const formatEntitySelectorLabel = (
  entity?: MappableEntity,
  entityKey?: string,
  selector?: string | null,
): string => {
  const sel = (selector ?? '').trim();
  const isParticipant =
    entityKey === 'participant' ||
    entity?.selectBy?.some((f) => f.value === 'beneficiaryLevel');

  // Default / empty selector represents the first row
  if (!sel || sel === '[0]') {
    if (isParticipant) {
      return PARTICIPANT_POSITION_LABELS[0];
    }
    return `1st ${entity?.label ?? 'row'}`;
  }

  // By position: [0], [1], [2], etc.
  const posMatch = sel.match(/^\[?(\d+)\]?$/);
  if (posMatch) {
    const idx = parseInt(posMatch[1], 10);
    if (isParticipant) {
      return (
        PARTICIPANT_POSITION_LABELS[idx] ??
        `${idx + 1}th Beneficiary / Participant`
      );
    }
    const ordinals = ['1st', '2nd', '3rd', '4th', '5th'];
    const ord = ordinals[idx] || `${idx + 1}th`;
    return `${ord} ${entity?.label ?? 'row'}`;
  }

  // By match: [field=value]
  const match = parseMatch(sel);
  if (match.field && match.value) {
    const fieldDef = entity?.selectBy?.find((f) => f.value === match.field);
    const fieldLabel = fieldDef?.label ?? match.field;

    if (match.field === 'beneficiaryLevel') {
      const lvlNum = parseInt(match.value, 10);
      const lvlObj = BENEFICIARY_LEVELS.find(
        (l) =>
          l.value === lvlNum ||
          l.label.toLowerCase() === match.value.toLowerCase() ||
          (match.value.toLowerCase() === 'primary' && l.value === 1) ||
          (match.value.toLowerCase() === 'secondary' && l.value === 2) ||
          (match.value.toLowerCase() === 'tertiary' && l.value === 3) ||
          (match.value.toLowerCase() === 'quaternary' && l.value === 4),
      );
      return lvlObj
        ? `${lvlObj.label}`
        : `Level ${match.value} beneficiary`;
    }

    return `${fieldLabel}: "${match.value}"`;
  }

  return sel;
};

const EntityRowSelector: React.FC<EntityRowSelectorProps> = ({
  entity,
  entityKey,
  value,
  onChange,
  resetKey,
  compact = false,
}) => {
  const canMatch = !!entity.selectBy?.length;
  const isParticipant =
    entityKey === 'participant' ||
    entity.selectBy?.some((f) => f.value === 'beneficiaryLevel');

  const [mode, setMode] = React.useState<'position' | 'match'>(
    value.includes('=') ? 'match' : 'position',
  );
  const match = parseMatch(value);

  // Deliberately not derived from the value on every render: choosing "by a
  // matching value" clears the selector until a field and a value are picked,
  // and re-deriving would snap the control back to position mid-edit.
  const latestValue = React.useRef(value);
  latestValue.current = value;
  React.useEffect(() => {
    setMode(latestValue.current.includes('=') ? 'match' : 'position');
  }, [resetKey]);

  const setMatch = (field: string, matchValue: string) => {
    onChange(field && matchValue ? `[${field}=${matchValue}]` : '');
  };

  const isBeneficiaryLevelField = match.field === 'beneficiaryLevel';

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {canMatch && (
        <Select
          value={mode}
          onChange={(next: 'position' | 'match') => {
            setMode(next);
            onChange('');
          }}
          style={{ width: '100%' }}
        >
          <Option value="position">By position</Option>
          <Option value="match">By a matching value</Option>
        </Select>
      )}

      {mode === 'position' || !canMatch ? (
        <Select
          value={value || '[0]'}
          onChange={onChange}
          style={{ width: '100%' }}
        >
          {['1st', '2nd', '3rd', '4th', '5th'].map((label, index) => (
            <Option key={index} value={`[${index}]`}>
              {isParticipant && PARTICIPANT_POSITION_LABELS[index]
                ? PARTICIPANT_POSITION_LABELS[index]
                : `${label} ${entity.label}`}
            </Option>
          ))}
        </Select>
      ) : (
        <Space.Compact style={{ width: '100%' }}>
          <Select
            value={match.field || undefined}
            onChange={(field: string) => {
              // Default to '1' for beneficiary level on field switch
              const defaultVal = field === 'beneficiaryLevel' ? '1' : '';
              setMatch(field, defaultVal);
            }}
            placeholder="Field..."
            style={{ width: '50%' }}
          >
            {entity.selectBy?.map((f) => (
              <Option key={f.value} value={f.value}>
                {f.label}
              </Option>
            ))}
          </Select>
          {isBeneficiaryLevelField ? (
            <Select
              value={match.value || '1'}
              onChange={(val: string) => setMatch(match.field, val)}
              placeholder="Select level..."
              style={{ width: '50%' }}
            >
              {BENEFICIARY_LEVELS.map((lvl) => (
                <Option key={lvl.value} value={String(lvl.value)}>
                  {lvl.label}
                </Option>
              ))}
            </Select>
          ) : (
            <Input
              value={match.value}
              onChange={(e) => setMatch(match.field, e.target.value)}
              placeholder="equals..."
              style={{ width: '50%' }}
            />
          )}
        </Space.Compact>
      )}

      {!compact && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          An estate holds several. Leave it on the 1st to fill from whichever
          comes first.
        </Text>
      )}
    </Space>
  );
};

export default EntityRowSelector;
